import { X } from 'lucide-react';
import { useGetVendorById } from '@/services/vendorService';

interface VendorInfoCardProps {
  vendorId: string;
  onClose: () => void;
}

const getInitials = (name?: string) => {
  if (!name) return 'V';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const VendorInfoCard = ({ vendorId, onClose }: VendorInfoCardProps) => {
  const { data: vendor, isLoading } = useGetVendorById(vendorId);

  const addressLine1 = vendor?.address1?.trim() ?? '';
  const addressLine2 = [vendor?.city, vendor?.state, vendor?.zipCode]
    .filter((part) => part && part.toString().trim().length > 0)
    .join(', ');

  return (
    <div
      data-testid="vendor-info-card"
      className="fixed bottom-6 right-6 z-50 w-[360px] rounded-xl border border-gray-200 bg-white shadow-xl overflow-hidden"
    >
      <div className="flex items-start justify-between px-4 pt-4 pb-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#E9D8FD] text-[#553C9A] flex items-center justify-center text-sm font-semibold">
            {getInitials(vendor?.name)}
          </div>
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-sm font-semibold text-gray-900 truncate">
              {isLoading ? 'Loading vendor…' : (vendor?.name ?? 'Vendor')}
            </span>
            {addressLine1 ? <span className="text-xs text-gray-500 leading-snug">{addressLine1}</span> : null}
            {addressLine2 ? <span className="text-xs text-gray-500 leading-snug">{addressLine2}</span> : null}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close vendor information"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mx-4 border-t border-gray-200" />

      <div className="px-4 py-3 flex flex-col gap-2 text-sm">
        <div className="flex justify-between items-center">
          <span className="font-medium text-gray-700">Tax ID:</span>
          <span className="text-gray-900">{vendor?.taxId ?? '-'}</span>
        </div>
        <div className="flex justify-between items-center gap-3">
          <span className="font-medium text-gray-700">Email address:</span>
          <span className="text-gray-900 truncate">{vendor?.email ?? '-'}</span>
        </div>
      </div>
    </div>
  );
};

export default VendorInfoCard;
